import { useState } from 'react';
import { resolveAuthErrorI18nKey, type AuthErrorI18nKey } from '@entities/auth';
import { createApiClient } from '@shared/api/client/api-client';
import { createAuthApi } from '@shared/api/lib/auth-api';

import { env } from '@shared/config/env';

interface UseVerifyEmailResult {
  isLoading: boolean;
  errorI18nKey: AuthErrorI18nKey | null;
  errorMessage: string | null;
  sendEmail: () => Promise<boolean>;
}

export function useVerifyEmail(): UseVerifyEmailResult {
  const [isLoading, setIsLoading] = useState(false);
  const [errorI18nKey, setErrorI18nKey] = useState<AuthErrorI18nKey | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sendEmail = async (): Promise<boolean> => {
    setIsLoading(true);
    setErrorI18nKey(null);
    setErrorMessage(null);

    try {
      const apiClient = createApiClient({ baseURL: env.API_BASE_URL });
      const authApi = createAuthApi(apiClient);

      await authApi.sendEmail();
      return true;
    } catch (err: unknown) {
      const rawMessage = err instanceof Error ? err.message : undefined;
      const i18nKey = resolveAuthErrorI18nKey(rawMessage);

      if (i18nKey) {
        setErrorI18nKey(i18nKey);
      } else {
        setErrorMessage(rawMessage || null);
        setErrorI18nKey('errors.verificationFailed');
      }

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, errorI18nKey, errorMessage, sendEmail };
}
